<script lang="ts" setup>
export type VButtonTypes = 'submit' | 'button';
export type VButtonSize = 'small' | 'normal' | 'large';
export type VButtonTarget = '_blank' | '_self';

export interface VButtonEmits {
    // eslint-disable-next-line no-unused-vars
    (e: 'click', data: Event): void;
}
export interface VButtonProps {
    color?: Colors
    to?: VNuxtLinkTo
    type?: VButtonTypes
    size?: VButtonSize
    disabled?: boolean
    ariaExpanded?: boolean | undefined
    ariaPressed?: boolean | undefined
    ariaSelected?: boolean | undefined
    role?: string
    loading?: boolean
    classes?: string
    external?: boolean
    target?: VButtonTarget
    label?: string
}

const emit = defineEmits<VButtonEmits>()

const props = withDefaults(defineProps<VButtonProps>(), {
    color: 'secondary',
    to: undefined,
    size: 'normal',
    ariaExpanded: undefined,
    ariaPressed: undefined,
    ariaSelected: undefined,
    role: undefined,
    loading: false,
    type: 'submit',
    classes: 'align-items-center',
    external: true,
    target: '_self',
    label: '',
    disabled: false,
})

const getClasses = () =>
    `btn-${props.color} btn-${props.size} ${props.classes} ${
        props.loading ? 'is-loading' : ''
    } ${props.disabled ? 'pe-none' : ''}`

const { data, sendEvent } = useTracker()

const handleClick = (e: Event) => {
    emit('click', e)
    if (props.label) {
        sendEvent({
            data: {
                ...data,
                click: props.label,
            },
        })
    }
}
</script>
<template>
  <NuxtLink
    v-if="to"
    :external="props.external"
    :target="props.target"
    :to="props.to"
    :role="props.role"
    class="btn m-0 d-inline-flex position-relative overflow-hidden justify-content-center align-items-center transition"
    :class="getClasses()"
    :aria-expanded="props.ariaExpanded"
    :aria-pressed="props.ariaPressed"
    :aria-selected="props.ariaSelected"
    @click="handleClick"
  >
    <slot />
    <span
      v-if="props.target === '_blank'"
      class="visually-hidden"
    >
      {{ $t(" (externý odkaz)") }}
    </span>
    <svg
      v-if="props.target === '_blank'"
      class="btn-icon d-block ms-3 flex-shrink-0"
      width="16"
      height="16"
    >
      <use xlink:href="#external" />
    </svg>
  </NuxtLink>
  <button
    v-else
    :type="props.type"
    :role="props.role"
    class="btn m-0 position-relative overflow-hidden d-inline-flex justify-content-center transition"
    :class="getClasses()"
    :disabled="props.disabled || props.loading"
    :aria-expanded="props.ariaExpanded"
    :aria-pressed="props.ariaPressed"
    :aria-selected="props.ariaSelected"
    @click="handleClick"
  >
    <slot />
    <LoadingBar
      v-if="props.loading"
      :color="props.color"
    />
  </button>
</template>
<style>
@import "./Button.vars.scss";
</style>
<style scoped lang="scss">
@import "./Button.scss";
</style>
